// src/pages/SettingsPage.js
import { useEffect, useState, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';

const BboxMap = lazy(() => import('./BboxMap'));

// Taxa search (iNaturalist /v1/taxa) — module-level so they stay referentially
// stable across renders (and out of hook dependency arrays).
const TAXA_PER_PAGE = 200;        // /v1/taxa caps per_page at 200
const TAXA_MAX_PAGES = 5;         // safety cap → at most 1000 matches per fetch
const TAXA_HARD_CAP = 1200;       // max total taxa kept in the result list
const FAMILY_EXPAND_LIMIT = 6;    // max matched families whose genera we pull in
const PLANTAE_TAXON_ID = 47126;   // restrict matches to descendants of Plantae
const RANK_ORDER = { family: 0, genus: 1, species: 2 };

const DISTRIBUTION_COLORS = [
  '#7cb342',  // top species - bright green
  '#9ccc65',
  '#c5e1a5',
  '#a5d6a7',
  '#81c784',
  '#b5b5b5',  // "other" - light grey
];

const COMMON_NAME_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'nl', label: 'Dutch' },
  { code: 'sv', label: 'Swedish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'da', label: 'Danish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'pl', label: 'Polish' },
  { code: 'cs', label: 'Czech' },
  { code: 'sk', label: 'Slovak' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'el', label: 'Greek' },
  { code: 'hr', label: 'Croatian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'ru', label: 'Russian' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'ar', label: 'Arabic' },
  { code: 'he', label: 'Hebrew' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh-CN', label: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: 'Chinese (Traditional)' },
  { code: 'th', label: 'Thai' },
];

function SettingsPage() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('genus'); // 'genus' or 'country'

  // Genus mode state
  const [allGenera, setAllGenera] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedGenera, setSelectedGenera] = useState([]);
  const [loadingGenera, setLoadingGenera] = useState(true);
  const [visibleCount, setVisibleCount] = useState(100);

  // Country mode state
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [bbox, setBbox] = useState(null); // { nelat, nelng, swlat, swlng } | null
  const bboxMapRef = useRef(null);

  // Taxa mode state (free choice of families / genera / species)
  const [taxaQuery, setTaxaQuery] = useState('');
  const [taxaResults, setTaxaResults] = useState([]); // full match set, sorted family→genus→species, A–Z
  const [taxaSearching, setTaxaSearching] = useState(false);
  const [taxaTruncated, setTaxaTruncated] = useState(false); // true if results were capped
  const [taxaVisible, setTaxaVisible] = useState(15);  // how many of the sorted results are shown
  const [selectedTaxa, setSelectedTaxa] = useState([]); // [{ id, name, rank, observations_count, preferred_common_name }]

  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [alpha, setAlpha] = useState(0.1);
  const [alphaDisplay, setAlphaDisplay] = useState(0.1);
  const [numRounds, setNumRounds] = useState(5);
  const [genusInfo, setGenusInfo] = useState({});
  const [hoveredGenus, setHoveredGenus] = useState(null);
  const [guessMode, setGuessMode] = useState('species'); // 'species' or 'genus'
  const [serverStatus, setServerStatus] = useState(null);
  const [commonNameLanguage, setCommonNameLanguage] = useState('en');
  const [commonNameLanguages, setCommonNameLanguages] = useState(COMMON_NAME_LANGUAGES);

  // Fetch server status for wait time estimate
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/get_server_status`);
        const data = await res.json();
        if (res.ok) setServerStatus(data);
      } catch (_) {}
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch genera and countries once on mount
  useEffect(() => {
    const fetchGenera = async () => {
      try {
        const res = await fetch(API_BASE + '/get_all_genera');
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Failed to load genera');
        setAllGenera(data.genera || []);
      } catch (err) {
        console.error('Error loading genera:', err);
        setError(err.message || 'Failed to load genera');
      } finally {
        setLoadingGenera(false);
      }
    };

    const fetchCountries = async () => {
      try {
        const res = await fetch(API_BASE + '/get_countries');
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Failed to load countries');
        setAllCountries(data.countries || []);
      } catch (err) {
        console.error('Error loading countries:', err);
      } finally {
        setLoadingCountries(false);
      }
    };

    const fetchCommonNameLanguages = async () => {
      try {
        const res = await fetch(API_BASE + '/get_common_name_languages');
        const data = await res.json();
        if (res.ok && Array.isArray(data.languages) && data.languages.length > 0) {
          setCommonNameLanguages(data.languages);
        }
      } catch (err) {
        console.error('Error loading common name languages:', err);
      }
    };

    fetchGenera();
    fetchCountries();
    fetchCommonNameLanguages();
  }, []);

  // Fetch one page of plant taxon matches from the iNaturalist search API.
  // NB: /v1/taxa ignores iconic_taxa, so we constrain to plants via
  // taxon_id=Plantae (a server-side descendant filter).
  const fetchTaxaPage = useCallback(async (q, page, signal) => {
    const url = `https://api.inaturalist.org/v1/taxa?q=${encodeURIComponent(q)}`
      + `&rank=family,genus,species&taxon_id=${PLANTAE_TAXON_ID}&is_active=true`
      + `&order_by=rank_level&order=desc&per_page=${TAXA_PER_PAGE}&page=${page}`;
    const res = await fetch(url, signal ? { signal } : undefined);
    const data = await res.json();
    return { results: data.results || [], total: data.total_results || 0 };
  }, []);

  // Fetch all genera contained in a given family (paginated, capped).
  const fetchFamilyGenera = useCallback(async (familyId, signal) => {
    const genera = [];
    let truncated = false;
    for (let page = 1; page <= TAXA_MAX_PAGES; page++) {
      const url = `https://api.inaturalist.org/v1/taxa?taxon_id=${familyId}&rank=genus`
        + `&is_active=true&order_by=rank_level&order=desc&per_page=${TAXA_PER_PAGE}&page=${page}`;
      const res = await fetch(url, signal ? { signal } : undefined);
      const data = await res.json();
      const results = data.results || [];
      genera.push(...results);
      if (genera.length >= (data.total_results || 0) || results.length === 0) break;
      if (page === TAXA_MAX_PAGES) truncated = true;
    }
    return { genera, truncated };
  }, []);

  // Live taxon search, debounced. Fetches the full match set (up to the cap),
  // additionally pulls in the genera of any matched family, then sorts
  // families → genera → species, alphabetically within each rank.
  useEffect(() => {
    const q = taxaQuery.trim();
    if (q.length < 2) {
      setTaxaResults([]);
      setTaxaTruncated(false);
      setTaxaSearching(false);
      return undefined;
    }
    setTaxaSearching(true);
    setTaxaVisible(15);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const byId = new Map();
        let total = 0;
        for (let page = 1; page <= TAXA_MAX_PAGES; page++) {
          const { results, total: t } = await fetchTaxaPage(q, page, controller.signal);
          total = t;
          results.forEach(r => byId.set(r.id, r));
          if (byId.size >= total || results.length === 0) break;
        }
        let truncated = byId.size < total;

        // Also surface the genera contained in any matched family.
        const families = [...byId.values()].filter(t => t.rank === 'family');
        const toExpand = families.slice(0, FAMILY_EXPAND_LIMIT);
        if (families.length > toExpand.length) truncated = true;
        for (const fam of toExpand) {
          if (byId.size >= TAXA_HARD_CAP) { truncated = true; break; }
          const { genera, truncated: gTrunc } = await fetchFamilyGenera(fam.id, controller.signal);
          genera.forEach(g => byId.set(g.id, g));
          if (gTrunc) truncated = true;
        }

        const sorted = [...byId.values()].sort((a, b) =>
          (RANK_ORDER[a.rank] - RANK_ORDER[b.rank]) ||
          a.name.localeCompare(b.name)
        );
        setTaxaResults(sorted);
        setTaxaTruncated(truncated);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Taxa search failed:', err);
      } finally {
        setTaxaSearching(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [taxaQuery, fetchTaxaPage, fetchFamilyGenera]);

  const handleAddTaxon = (taxon) => {
    setSelectedTaxa(prev =>
      prev.some(t => t.id === taxon.id)
        ? prev
        : [...prev, {
            id: taxon.id,
            name: taxon.name,
            rank: taxon.rank,
            observations_count: taxon.observations_count,
            preferred_common_name: taxon.preferred_common_name || '',
          }]
    );
  };

  const handleRemoveTaxon = (id) => {
    setSelectedTaxa(prev => prev.filter(t => t.id !== id));
  };

  const handleStart = async () => {
    setError(null);

    // Always create a fresh session — handles case where user navigated back
    // from game (old session was ended) or arrived here without a valid session
    let activeSessionId;
    try {
      const startRes = await fetch(API_BASE + '/start_game');
      const startData = await startRes.json();
      if (!startRes.ok || !startData.session_id) throw new Error('Failed to start session');
      activeSessionId = startData.session_id;
    } catch (err) {
      setError('Failed to start session. Please try again.');
      return;
    }

    if (mode === 'country') {
      if (!bbox && !selectedCountry) {
        setError('Please select a country or draw a rectangle on the map.');
        return;
      }

      // A drawn rectangle overrides the dropdown. Otherwise a country may
      // itself be defined by a bbox (e.g. Antarctica) rather than a place_id.
      const regionBbox = bbox || selectedCountry?.bbox || null;
      const usePlaceId = !regionBbox && selectedCountry?.place_id != null;

      setStarting(true);
      try {
        const body = {
          alpha,
          num_rounds: numRounds,
          session_id: activeSessionId,
          guess_mode: guessMode,
          common_name_language: commonNameLanguage,
        };
        if (usePlaceId) {
          body.place_id = selectedCountry.place_id;
        } else {
          Object.assign(body, regionBbox);
        }
        const res = await fetch(API_BASE + '/prefetch_obs_by_country', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Failed to start country game');
        const baseParams = `rounds=${numRounds}&session=${activeSessionId}&guessMode=${guessMode}&mode=country&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLanguage)}`;
        // Include the country name for named-bbox regions (e.g. Antarctica) so
        // the game can label it; a hand-drawn rectangle has no name.
        const regionName = !bbox && selectedCountry?.name ? `&country=${encodeURIComponent(selectedCountry.name)}` : '';
        const navUrl = usePlaceId
          ? `/game?${baseParams}&place_id=${selectedCountry.place_id}&country=${encodeURIComponent(selectedCountry.name)}`
          : `/game?${baseParams}&nelat=${regionBbox.nelat.toFixed(4)}&nelng=${regionBbox.nelng.toFixed(4)}&swlat=${regionBbox.swlat.toFixed(4)}&swlng=${regionBbox.swlng.toFixed(4)}${regionName}`;
        navigate(navUrl);
      } catch (err) {
        console.error('Error during country start:', err);
        setError(err.message || 'Failed to start game');
        setStarting(false);
      }
      return;
    }

    if (mode === 'taxa') {
      if (selectedTaxa.length === 0) {
        setError('Please select at least one taxon.');
        return;
      }

      setStarting(true);
      try {
        const taxaPayload = selectedTaxa.map(t => ({ id: t.id, name: t.name, rank: t.rank }));
        const res = await fetch(API_BASE + '/prefetch_obs_by_taxa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taxa: taxaPayload,
            alpha,
            num_rounds: numRounds,
            session_id: activeSessionId,
            guess_mode: guessMode,
            common_name_language: commonNameLanguage,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Failed to start taxa game');
        const taxaParam = encodeURIComponent(JSON.stringify(taxaPayload));
        navigate(`/game?rounds=${numRounds}&session=${activeSessionId}&guessMode=${guessMode}&mode=taxa&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLanguage)}&taxa=${taxaParam}`);
      } catch (err) {
        console.error('Error during taxa start:', err);
        setError(err.message || 'Failed to start game');
        setStarting(false);
      }
      return;
    }

    // Genus mode
    if (selectedGenera.length === 0) {
      setError('Please select at least one genus.');
      return;
    }

    if (guessMode === 'genus' && selectedGenera.length < 2) {
      setError('Please select at least 2 genera for genus guessing mode.');
      return;
    }

    if (guessMode === 'species') {
      const singleSpeciesGenera = selectedGenera.filter(g => genusInfo[g]?.species_count === 1);
      if (singleSpeciesGenera.length > 0 && selectedGenera.length === singleSpeciesGenera.length) {
        setError('Genus only has one species, please select at least one more genus.');
        return;
      }
    }

    setStarting(true);

    try {
      // 1. Validate selected genera on the backend
      const genusResponse = await fetch(API_BASE + '/get_genus_ls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_input: selectedGenera.join(','), session_id: activeSessionId }),
      });

      const genusData = await genusResponse.json();

      if (!genusResponse.ok || genusData.error) {
        throw new Error(genusData.error || 'Failed to validate genera');
      }

      const validGenus = genusData.valid_genus || [];

      if (validGenus.length === 0) {
        setError('No valid genera selected. Please choose from the list.');
        setStarting(false);
        return;
      }

      // 2. Prefetch observations in the background (this returns quickly)
      await fetch(API_BASE + '/prefetch_obs_balanced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_input: validGenus,
          alpha,
          num_rounds: numRounds,
          session_id: activeSessionId,
          guess_mode: guessMode,
          common_name_language: commonNameLanguage,
        }),
      });

      // 3. Navigate to the game page
      navigate(`/game?rounds=${numRounds}&session=${activeSessionId}&guessMode=${guessMode}&mode=${mode}&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLanguage)}&genera=${encodeURIComponent(validGenus.join(','))}`);
    } catch (err) {
      console.error('Error during start:', err);
      setError(err.message || 'Failed to start game');
      setStarting(false);
    }
  };

  const fetchGenusInfo = async (genus) => {
    try {
      const res = await fetch(`${API_BASE}/get_genus_info?genus=${encodeURIComponent(genus)}`);
      const data = await res.json();
      if (data.species_count !== undefined) {
        setGenusInfo(prev => ({ ...prev, [genus]: data }));
      }
    } catch (err) {
      console.error('Error fetching genus info:', err);
    }
  };

  const handleAddGenus = (genus) => {
    if (!selectedGenera.includes(genus)) {
      setSelectedGenera([...selectedGenera, genus]);
      fetchGenusInfo(genus);
    }
  };

  const handleRemoveGenus = (genus) => {
    setSelectedGenera(selectedGenera.filter((g) => g !== genus));
  };

  const handleAddRandomGenus = () => {
    const unselected = allGenera.filter(g => !selectedGenera.includes(g));
    if (unselected.length === 0) return;
    const pick = unselected[Math.floor(Math.random() * unselected.length)];
    setSelectedGenera([...selectedGenera, pick]);
    fetchGenusInfo(pick);
  };

  const availableGenera = useMemo(() =>
    allGenera.filter((g) =>
      !selectedGenera.includes(g) &&
      g.toLowerCase().includes(filterText.toLowerCase())
    ),
    [allGenera, selectedGenera, filterText]
  );

  const visibleGenera = useMemo(() =>
    availableGenera.slice(0, visibleCount),
    [availableGenera, visibleCount]
  );

  // Mode toggle buttons
  const modeToggle = (
    <div style={{ marginBottom: '20px' }}>
      {['genus', 'country', 'taxa'].map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => { setMode(m); setError(null); }}
          style={{
            padding: '6px 18px',
            marginRight: '8px',
            fontWeight: mode === m ? 'bold' : 'normal',
            backgroundColor: mode === m ? 'var(--moss-dark)' : 'transparent',
            color: mode === m ? 'var(--text-light)' : 'var(--text-dark)',
            border: '1px solid rgba(74,107,56,0.4)',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {m === 'genus' ? 'By Genus' : m === 'country' ? 'By Country' : 'By Taxa'}
        </button>
      ))}
    </div>
  );

  // Country selector UI
  const countryContent = (
    <div style={{ marginBottom: '20px' }}>
      {loadingCountries ? (
        <p>Loading countries...</p>
      ) : (
        <div>
          <label style={{ fontSize: '1rem' }}>
            <strong>Select a country:</strong>
            <select
              value={selectedCountry ? String(selectedCountry.place_id ?? selectedCountry.name) : ''}
              onChange={(e) => {
                const country = allCountries.find(c => String(c.place_id ?? c.name) === e.target.value);
                setSelectedCountry(country || null);
              }}
              style={{
                marginLeft: '10px',
                padding: '6px 10px',
                fontSize: '1rem',
                backgroundColor: 'var(--moss-dark)',
                color: 'var(--text-light)',
                border: '1px solid rgba(200,223,168,0.4)',
                borderRadius: '4px',
              }}
            >
              <option value="">-- choose a country --</option>
              {allCountries.map((c) => (
                <option key={c.place_id ?? c.name} value={c.place_id ?? c.name}>{c.name}</option>
              ))}
            </select>
          </label>
          {selectedCountry && !bbox && (
            <p style={{ marginTop: '8px', color: 'var(--moss)', fontSize: '0.85rem' }}>
              Game will sample from the top 100 most observed plant species in {selectedCountry.name} on iNaturalist.
            </p>
          )}
        </div>
      )}
      <div style={{ marginTop: '20px', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
        <p style={{ fontSize: '0.9rem', color: 'var(--moss-dark)', marginBottom: '8px' }}>
          <strong>Or draw a region</strong> on the map — use the rectangle tool or the ✎ freehand tool (overrides country selection; the region's bounding box is used):
        </p>
        <Suspense fallback={<div style={{ height: '340px', background: 'rgba(74,107,56,0.05)', borderRadius: '8px', border: '1px solid rgba(74,107,56,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--moss)' }}>Loading map…</div>}>
          <BboxMap ref={bboxMapRef} onBboxChange={setBbox} selectedPlaceId={selectedCountry?.place_id} selectedRegionBbox={selectedCountry?.bbox} />
        </Suspense>
        {bbox ? (
          <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--moss-dark)', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
            <span>Custom region: N {bbox.nelat.toFixed(2)}° / S {bbox.swlat.toFixed(2)}° / E {bbox.nelng.toFixed(2)}° / W {bbox.swlng.toFixed(2)}°</span>
            <button
              type="button"
              onClick={() => { setBbox(null); bboxMapRef.current?.clear(); }}
              style={{ padding: '2px 8px', fontSize: '0.78rem', backgroundColor: 'transparent', color: '#c00', border: '1px solid rgba(200,0,0,0.3)', borderRadius: '4px', cursor: 'pointer' }}
            >
              Clear rectangle
            </button>
          </div>
        ) : (
          <p style={{ marginTop: '6px', fontSize: '0.82rem', color: 'rgba(74,107,56,0.5)' }}>
            No region drawn — country dropdown applies.
          </p>
        )}
      </div>
    </div>
  );

  // Taxa selector UI (free choice of families / genera / species)
  const RANK_COLORS = { family: '#8e6abf', genus: '#4a6b38', species: '#b06000' };
  const rankBadge = (rank) => (
    <span style={{
      fontSize: '0.65rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      color: '#fff',
      backgroundColor: RANK_COLORS[rank] || '#777',
      borderRadius: '3px',
      padding: '1px 5px',
      letterSpacing: '0.04em',
    }}>{rank}</span>
  );

  const taxaContent = (
    <div>
      {/* Search input */}
      <div style={{ marginBottom: '16px' }}>
        <label>
          Search taxa:
          <input
            type="text"
            value={taxaQuery}
            onChange={(e) => setTaxaQuery(e.target.value)}
            placeholder="Type a family, genus or species (e.g., Fagaceae, Quercus, Quercus robur)"
            style={{ width: '340px', maxWidth: '80%', marginLeft: '8px' }}
          />
        </label>
      </div>

      {/* Search results */}
      <div
        style={{
          marginBottom: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          width: '60%',
          marginLeft: 'auto',
          marginRight: 'auto',
          maxHeight: '220px',
          overflowY: 'auto',
          textAlign: 'left',
        }}
      >
        <h3 style={{
          position: 'sticky', top: 0, backgroundColor: '#f5f5f5', margin: 0,
          padding: '10px', borderBottom: '1px solid #ccc', zIndex: 1,
        }}>Results (click to add):</h3>
        <div style={{ padding: '0 10px 10px' }}>
          {taxaQuery.trim().length < 2 ? (
            <p style={{ color: 'var(--moss)' }}>Type at least 2 characters to search iNaturalist.</p>
          ) : taxaSearching ? (
            <p style={{ color: 'var(--moss)' }}>Searching…</p>
          ) : taxaResults.length === 0 ? (
            <p style={{ color: 'var(--moss)' }}>No families, genera or species match.</p>
          ) : (
            <>
              {taxaResults.slice(0, taxaVisible).map((t) => {
                const added = selectedTaxa.some(s => s.id === t.id);
                return (
                  <div
                    key={t.id}
                    onClick={() => !added && handleAddTaxon(t)}
                    onMouseEnter={(e) => { if (!added) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '5px 6px', cursor: added ? 'default' : 'pointer',
                      borderBottom: '1px solid #eee', borderRadius: '2px',
                      opacity: added ? 0.45 : 1,
                    }}
                  >
                    {rankBadge(t.rank)}
                    <span style={{ fontStyle: t.rank === 'species' ? 'italic' : 'normal' }}>{t.name}</span>
                    {t.preferred_common_name && (
                      <span style={{ color: 'var(--moss)', fontSize: '0.8rem' }}>({t.preferred_common_name})</span>
                    )}
                    <span style={{ marginLeft: 'auto', color: 'var(--moss-dark)', fontSize: '0.78rem' }}>
                      {(t.observations_count ?? 0).toLocaleString()} obs
                    </span>
                  </div>
                );
              })}
              {taxaVisible < taxaResults.length && (
                <div
                  onClick={() => setTaxaVisible(v => v + 15)}
                  style={{
                    padding: '6px', cursor: 'pointer', color: 'var(--moss)',
                    textAlign: 'center', fontStyle: 'italic',
                  }}
                >
                  Show more ({taxaResults.length - taxaVisible} more)
                </div>
              )}
              {taxaVisible >= taxaResults.length && taxaTruncated && (
                <p style={{ padding: '6px', margin: 0, textAlign: 'center', fontSize: '0.78rem', color: 'rgba(74,107,56,0.6)' }}>
                  Some matches were omitted — refine your search to narrow it down.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Selected taxa box */}
      <div
        style={{
          marginTop: '20px', marginBottom: '10px', border: '1px solid #ccc',
          borderRadius: '4px', padding: '10px', width: '60%',
          marginLeft: 'auto', marginRight: 'auto', minHeight: '50px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
          <h3 style={{ margin: 0 }}>Selected taxa:</h3>
          <button
            type="button"
            onClick={() => selectedTaxa.length > 0 && setSelectedTaxa([])}
            disabled={selectedTaxa.length === 0}
            style={{
              padding: '3px 10px', fontSize: '0.82rem', backgroundColor: 'transparent',
              color: selectedTaxa.length > 0 ? '#c00' : '#bbb',
              border: `1px solid ${selectedTaxa.length > 0 ? 'rgba(200,0,0,0.3)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '4px', cursor: selectedTaxa.length > 0 ? 'pointer' : 'default',
            }}
          >
            Remove all
          </button>
        </div>
        {selectedTaxa.length === 0 ? (
          <p style={{ color: 'var(--moss)' }}>No taxa selected yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
            {selectedTaxa.map((t) => (
              <div
                key={t.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  border: '2px solid rgba(74,107,56,0.5)', borderRadius: '8px',
                  padding: '6px 8px', fontSize: '0.9rem',
                }}
              >
                {rankBadge(t.rank)}
                <span style={{ fontStyle: t.rank === 'species' ? 'italic' : 'normal' }}>{t.name}</span>
                {(t.observations_count != null && t.observations_count < 50) && (
                  <span style={{ fontSize: '0.7rem', color: '#c00', fontWeight: 'bold' }}>&lt;50 obs!</span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveTaxon(t.id)}
                  style={{
                    border: 'none', background: 'transparent', color: '#c00',
                    cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem',
                    lineHeight: 1, padding: '0 2px',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: '0.8rem', color: 'var(--moss)', marginTop: '10px' }}>
          The game samples observations from all selected taxa (families and genera expand to their species).
        </p>
      </div>
    </div>
  );

  let mainContent;
  if (mode === 'country') {
    mainContent = countryContent;
  } else if (mode === 'taxa') {
    mainContent = taxaContent;
  } else if (loadingGenera) {
    mainContent = <p>Loading genera...</p>;
  } else if (error && allGenera.length === 0) {
    mainContent = <p style={{ color: 'red' }}>Error: {error}</p>;
  } else {
    mainContent = (
      <div>
        {/* Filter input */}
        <div style={{ marginBottom: '20px' }}>
          <label>
            Filter genera:
            <input
              type="text"
              value={filterText}
              onChange={(e) => { setFilterText(e.target.value); setVisibleCount(100); }}
              placeholder="Type to filter (e.g., Acer)"
              style={{ width: '220px', marginLeft: '8px' }}
            />
          </label>
        </div>

        {/* Available genera list */}
        <div
          style={{
            marginBottom: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '60%',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxHeight: '220px',
            overflowY: 'auto',
            textAlign: 'left',
          }}
        >
          <h3 style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#f5f5f5',
            margin: 0,
            padding: '10px',
            borderBottom: '1px solid #ccc',
            zIndex: 1,
          }}>Available genera (click to add):</h3>
          <div style={{ padding: '0 10px 10px' }}>
          {availableGenera.length === 0 ? (
            <p style={{ color: 'var(--moss)' }}>
              No genera match the filter or all are selected.
            </p>
          ) : (
            <>
              {visibleGenera.map((genus) => (
                <div
                  key={genus}
                  onClick={() => handleAddGenus(genus)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  style={{
                    padding: '4px 6px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    borderRadius: '2px',
                  }}
                >
                  {genus}
                </div>
              ))}
              {visibleCount < availableGenera.length && (
                <div
                  onClick={() => setVisibleCount(visibleCount + 100)}
                  style={{
                    padding: '6px',
                    cursor: 'pointer',
                    color: 'var(--moss)',
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  ... {availableGenera.length - visibleCount} more — click to load
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* Selected genera box */}
        <div
          style={{
            marginTop: '20px',
            marginBottom: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
            width: '60%',
            marginLeft: 'auto',
            marginRight: 'auto',
            minHeight: '50px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
            <h3 style={{ margin: 0 }}>Selected genera:</h3>
            <button
              type="button"
              onClick={handleAddRandomGenus}
              disabled={allGenera.length === selectedGenera.length}
              style={{
                padding: '3px 10px',
                fontSize: '0.82rem',
                backgroundColor: 'var(--moss-dark)',
                color: 'var(--text-light)',
                border: '1px solid rgba(200,223,168,0.4)',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              + Random
            </button>
            <button
              type="button"
              onClick={() => selectedGenera.length > 0 && setSelectedGenera([])}
              disabled={selectedGenera.length === 0}
              style={{
                padding: '3px 10px',
                fontSize: '0.82rem',
                backgroundColor: 'transparent',
                color: selectedGenera.length > 0 ? '#c00' : '#bbb',
                border: `1px solid ${selectedGenera.length > 0 ? 'rgba(200,0,0,0.3)' : 'rgba(0,0,0,0.12)'}`,
                borderRadius: '4px',
                cursor: selectedGenera.length > 0 ? 'pointer' : 'default',
              }}
            >
              Remove all
            </button>
          </div>
          {selectedGenera.length === 0 ? (
            <p style={{ color: 'var(--moss)' }}>No genera selected yet.</p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
              }}
            >
              {selectedGenera.map((genus) => {
                const info = genusInfo[genus];
                return (
                  <div
                    key={genus}
                    style={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredGenus(genus)}
                    onMouseLeave={() => setHoveredGenus(null)}
                  >
                    <div
                      style={{
                        border: '2px solid rgba(74,107,56,0.5)',
                        borderRadius: '8px',
                        padding: '8px 10px',
                        backgroundColor: 'transparent',
                        color: 'var(--text-dark)',
                        fontSize: '0.9rem',
                        minWidth: '140px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <strong>{genus}</strong>
                          {info && (
                            info.total_observations == null
                              ? <span style={{ fontSize: '0.7rem', color: '#c00', fontWeight: 'bold', whiteSpace: 'nowrap' }}>No obs!</span>
                              : info.total_observations < 50
                                ? <span style={{ fontSize: '0.7rem', color: '#c00', fontWeight: 'bold', whiteSpace: 'nowrap' }}>&lt;50 obs!</span>
                                : null
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveGenus(genus)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#c00',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            padding: '2px 4px',
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {info ? (
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--moss-dark)', marginBottom: '3px' }}>
                            {info.species_count} species · {info.total_observations?.toLocaleString() ?? '–'} obs
                          </div>
                          {info.distribution.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                height: '10px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                backgroundColor: 'rgba(0,0,0,0.1)',
                              }}
                            >
                              {info.distribution.map((d, i) => (
                                <div key={i} style={{
                                  width: `${d.fraction * 100}%`,
                                  backgroundColor: DISTRIBUTION_COLORS[i] || DISTRIBUTION_COLORS[5],
                                }} />
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.75rem', color: 'var(--moss)', marginTop: '4px' }}>
                          Loading…
                        </div>
                      )}
                    </div>
                    {/* Custom tooltip */}
                    {hoveredGenus === genus && info && info.distribution.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginTop: '8px',
                        backgroundColor: 'var(--moss-dark)',
                        color: 'var(--text-light)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        zIndex: 100,
                        boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
                        pointerEvents: 'none',
                      }}>
                        {/* Triangle arrow */}
                        <div style={{
                          position: 'absolute',
                          top: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderBottom: '6px solid var(--moss-dark)',
                        }} />
                        {info.distribution.map((d, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '1px 0',
                          }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '2px',
                              backgroundColor: DISTRIBUTION_COLORS[i] || DISTRIBUTION_COLORS[5],
                              flexShrink: 0,
                            }} />
                            <span>{d.name}: {d.observations.toLocaleString()} ({(d.fraction * 100).toFixed(2)}%)</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-title-btn" onClick={() => navigate('/')}>
          <img src="/logo.png?v=2" alt="" className="header-logo" />
          PlantID Game
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px', position: 'relative' }}>
      <h1>Settings</h1>

      {modeToggle}

      {mainContent}

      {error && (mode === 'country' || mode === 'taxa' || (allGenera.length > 0 && !loadingGenera)) && (
        <div style={{ marginTop: '10px' }}>
          <p style={{ color: 'red' }}>Error: {error}</p>
        </div>
      )}

      {(mode === 'genus' || mode === 'country' || mode === 'taxa') && (
        <div style={{ marginTop: '20px', marginBottom: '10px' }}>
          <strong>Guess mode:</strong>
          <div style={{ marginTop: '6px' }}>
            {['species', 'genus'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setGuessMode(m); setError(null); }}
                style={{
                  padding: '6px 18px',
                  marginRight: '8px',
                  fontWeight: guessMode === m ? 'bold' : 'normal',
                  backgroundColor: guessMode === m ? 'var(--moss-dark)' : 'transparent',
                  color: guessMode === m ? 'var(--text-light)' : 'var(--text-dark)',
                  border: '1px solid rgba(74,107,56,0.4)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {m === 'species' ? 'Guess Species' : 'Guess Genus'}
              </button>
            ))}
          </div>
        </div>
      )}

      {(mode === 'genus' || mode === 'country' || mode === 'taxa') && (
        <div style={{ marginTop: '20px', marginBottom: '10px' }}>
          <label>
            <strong>Common name language:</strong>
            <select
              value={commonNameLanguage}
              onChange={(e) => setCommonNameLanguage(e.target.value)}
              style={{
                marginLeft: '10px',
                padding: '6px 10px',
                fontSize: '1rem',
                backgroundColor: 'var(--moss-dark)',
                color: 'var(--text-light)',
                border: '1px solid rgba(200,223,168,0.4)',
                borderRadius: '4px',
              }}
            >
              {commonNameLanguages.map((language) => (
                <option key={language.code} value={language.code}>{language.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div style={{ marginTop: '30px', marginBottom: '20px' }}>
        <label>
          <strong title="Maximum 25 rounds" style={{ cursor: 'help' }}>Number of rounds:</strong>
          <input
            type="number"
            min="1"
            max="25"
            value={numRounds}
            onChange={(e) => setNumRounds(Math.max(1, Math.min(25, parseInt(e.target.value) || 1)))}
            style={{ width: '60px', marginLeft: '8px' }}
          />
        </label>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label>
          <strong>Species variety:</strong>
          <span style={{ marginLeft: '8px', marginRight: '8px' }}>Popular</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={alphaDisplay}
            onChange={(e) => setAlphaDisplay(parseFloat(e.target.value))}
            onPointerUp={(e) => setAlpha(parseFloat(e.target.value))}
          />
          <span style={{ marginLeft: '8px' }}>Rare</span>
        </label>
        <p style={{ fontSize: '0.85rem', color: 'var(--moss)', marginTop: '4px' }}>
          Controls how often rare species appear (current: {alphaDisplay.toFixed(2)})
        </p>
      </div>

      <div style={{ marginTop: '10px' }}>
        <button
          onClick={handleStart}
          style={{ padding: '11.5px 23px', fontSize: '18.4px', border: '2px solid var(--moss-dark)' }}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Start'}
        </button>
        {serverStatus && (() => {
          const INAT_INTERVAL = serverStatus.inat_min_interval;
          const REQUESTS_PER_ROUND = 4; // taxon ID + observations + family + common names
          const IMAGE_SEC = 2;
          const otherSessions = serverStatus.active_prefetch_sessions;
          // Each other session has on average half its rounds left in the queue
          const queuedRequests = otherSessions * REQUESTS_PER_ROUND + Math.round(serverStatus.inat_cooldown_remaining / INAT_INTERVAL);
          const firstRoundSec = Math.round((queuedRequests + REQUESTS_PER_ROUND) * INAT_INTERVAL + IMAGE_SEC);
          const allRoundsSec = Math.round(firstRoundSec + (numRounds - 1) * (REQUESTS_PER_ROUND * INAT_INTERVAL + IMAGE_SEC));
          const hasOtherUsers = otherSessions > 0;
          return (
            <p style={{ marginTop: '8px', fontSize: '0.8rem', color: hasOtherUsers ? '#b06000' : 'var(--moss)', margin: '8px auto 0' }}>
              ⏱ First round ready in ~{firstRoundSec}s · all {numRounds} rounds: ~{allRoundsSec}s
              {hasOtherUsers && (
                <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px' }}>
                  ({otherSessions} other user{otherSessions > 1 ? 's' : ''} currently loading)
                </span>
              )}
            </p>
          );
        })()}
      </div>

      {/* Waiting overlay when starting */}
      {starting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--moss-dark)',
              border: '1px solid rgba(200,223,168,0.3)',
              padding: '28px 48px',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              color: 'var(--text-light)',
              fontFamily: "'Georgia', serif",
            }}
          >
            <div
              style={{
                margin: '0 auto 18px',
                width: '36px',
                height: '36px',
                border: '4px solid rgba(200,223,168,0.25)',
                borderTopColor: 'var(--moss-pale)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p style={{ margin: 0, color: 'var(--moss-pale)', fontSize: '1rem' }}>
              Preparing your game… Please wait.
            </p>
          </div>
          {/* Simple inline keyframes for the spinner */}
          <style>
            {`@keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }`}
          </style>
        </div>
      )}
      </div>
    </div>
  );
}

export default SettingsPage;
